/**
 * The briefing's paragraphs are plain prose with entity refs written as
 * markdown links (`[Ana Souza](/clients/fn_1 "Client · H-1B")`) and the
 * occasional `**bold**`. Split a paragraph into typed segments so the view
 * can render refs as chips without a markdown engine.
 */

export type BriefingSegment =
  | { type: "text"; text: string; bold?: boolean }
  | { type: "ref"; label: string; href: string; title: string | null };

const REF_RE = /\[((?:[^[\]\\]|\\.)+)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;

const splitBold = (text: string): BriefingSegment[] => {
  const out: BriefingSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(BOLD_RE)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ type: "text", text: text.slice(last, at) });
    out.push({ type: "text", text: m[1], bold: true });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ type: "text", text: text.slice(last) });
  return out;
};

export const splitBriefing = (paragraph: string): BriefingSegment[] => {
  const out: BriefingSegment[] = [];
  let last = 0;
  for (const m of paragraph.matchAll(REF_RE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(...splitBold(paragraph.slice(last, at)));
    out.push({
      type: "ref",
      label: m[1].replace(/\\([[\]])/g, "$1"),
      href: m[2],
      title: m[3] ?? null
    });
    last = at + m[0].length;
  }
  if (last < paragraph.length) out.push(...splitBold(paragraph.slice(last)));
  return out;
};

/** The paragraph as plain text (refs reduced to their labels). */
export const briefingPlainText = (paragraph: string): string =>
  splitBriefing(paragraph)
    .map((s) => (s.type === "ref" ? s.label : s.text))
    .join("");
